"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@ui/button";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setDialog({
        title: options?.title ?? "Are you sure?",
        description: options?.description ?? "",
        confirmLabel: options?.confirmLabel ?? "Confirm",
        cancelLabel: options?.cancelLabel ?? "Cancel",
        destructive: options?.destructive !== false,
      });
    });
  }, []);

  const close = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setDialog(null);
  }, []);

  useEffect(() => {
    if (!dialog) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [dialog, close]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div
          role="presentation"
          onClick={() => close(false)}
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-150"
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-white rounded-2xl border border-border shadow-xl p-6 animate-in zoom-in-95 duration-150"
          >
            <div className="flex items-start gap-3 mb-3">
              {dialog.destructive && (
                <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                  <AlertTriangle size={17} className="text-red-500" />
                </div>
              )}
              <div className="min-w-0 pt-0.5">
                <h2 id="confirm-dialog-title" className="font-bold text-ink text-base leading-snug">
                  {dialog.title}
                </h2>
                {dialog.description && (
                  <p className="text-sm text-muted-foreground mt-1.5">{dialog.description}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => close(false)} className="cursor-pointer">
                {dialog.cancelLabel}
              </Button>
              <Button
                type="button"
                autoFocus
                onClick={() => close(true)}
                className={`cursor-pointer border-0 text-white ${dialog.destructive ? "bg-red-500 hover:bg-red-600" : "bg-primary hover:bg-primary/90"}`}
              >
                {dialog.confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

const CONFIRM_FALLBACK = () => Promise.resolve(false);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  return ctx ?? CONFIRM_FALLBACK;
}
